import { Button, Card, Flex, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PlusOutlined } from '@ant-design/icons';
import { useAdminObligationController_create } from '@api-client/generated/AdminObligationController/create';
import { useAdminObligationController_deleteOneById } from '@api-client/generated/AdminObligationController/deleteOneById';
import { useAdminObligationController_findAll } from '@api-client/generated/AdminObligationController/findAll';
import { useAdminObligationController_updateOneById } from '@api-client/generated/AdminObligationController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { useCurrentCompanyId } from '@hooks';
import { isPreviewPending, PREVIEW_POLL_INTERVAL_MS } from '@utils';

import TaxEditModal from './TaxEditModal';
import TaxTable from './TaxTable';
import * as S from './styled';

type Obligation = Schemas.AdminObligationResponseDto;

const OBLIGATION_GROUPS = {
  annual_report: {
    key: 'annual_report',
    title: 'Annual Reports',
  },
  vat_return: {
    key: 'vat_return',
    title: 'VAT Returns',
  },
  non_tax_payment: {
    key: 'non_tax_payment',
    title: 'Other Payments',
  },
} as const;

export const Taxes = () => {
  const companyId = useCurrentCompanyId();
  const navigate = useNavigate();

  const [activeKey, setActiveKey] = useState(() =>
    new Date().getFullYear().toString()
  );

  const [selectedObligation, setSelectedObligation] = useState<
    Obligation | undefined
  >(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const {
    data: obligations,
    refetch: refetchObligations,
    isLoading,
  } = useAdminObligationController_findAll({
    params: {
      companyId,
    },
    config: {
      // Poll while any obligation file's HEIC→JPEG preview is still generating so
      // it appears without a manual reload; stop once none are pending.
      refetchInterval: (query) =>
        query.state.data?.some((obligation) =>
          obligation.files.some(isPreviewPending)
        )
          ? PREVIEW_POLL_INTERVAL_MS
          : false,
    },
  });

  const { mutateAsync: updateObligation, isPending: isUpdating } =
    useAdminObligationController_updateOneById();
  const { mutateAsync: createObligation, isPending: isCreating } =
    useAdminObligationController_create();
  const { mutateAsync: deleteObligation, isPending: isDeleting } =
    useAdminObligationController_deleteOneById();

  const isFormLoading = isUpdating || isCreating || isDeleting;

  const years = useMemo(() => {
    if (!obligations) return [];

    const uniqueYears = new Set<number>();
    obligations.forEach((tax) => {
      if (tax.group === 'annual_report') {
        uniqueYears.add(tax.period.year + 1);
      } else {
        uniqueYears.add(tax.period.year);
      }
    });

    return Array.from(uniqueYears).sort((a, b) => b - a);
  }, [obligations]);

  const getTaxesByGroup = (
    year: number,
    groupKey: keyof typeof OBLIGATION_GROUPS
  ) =>
    (obligations || []).filter((tax) => {
      if (tax.group === OBLIGATION_GROUPS.annual_report.key) {
        return year === tax.period.year + 1 && tax.group === groupKey;
      } else {
        return tax.period.year === year && tax.group === groupKey;
      }
    });

  const handleOpenEditModal = (tax?: Obligation) => {
    setSelectedObligation(tax);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setSelectedObligation(undefined);
    setIsModalVisible(false);
  };

  const handleCreateObligation = async (
    data: Schemas.AdminCreateObligationDto
  ) => {
    try {
      await createObligation({
        parameter: {
          companyId: companyId,
        },
        requestBody: data,
      });
      await refetchObligations();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to create obligation:', error);
    }
  };

  const handleUpdateObligation = async (
    id: string,
    data: Schemas.AdminUpdateObligationDto
  ) => {
    try {
      await updateObligation({
        parameter: {
          companyId: companyId,
          id: id,
        },
        requestBody: data,
      });
      await refetchObligations();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to update obligation:', error);
    }
  };

  const handleDeleteObligation = async (id: string) => {
    try {
      await deleteObligation({
        parameter: {
          companyId: companyId,
          id: id,
        },
      });
      await refetchObligations();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to delete obligation:', error);
    }
  };

  const renderObligationGroupTables = (year: number) => (
    <Flex gap={36} vertical>
      {Object.values(OBLIGATION_GROUPS).map((group) => {
        const taxesInGroup = getTaxesByGroup(
          year,
          group.key as keyof typeof OBLIGATION_GROUPS
        );

        return (
          <Card
            title={
              group.key === OBLIGATION_GROUPS.vat_return.key ? (
                <Flex align="center" gap={16}>
                  <span>
                    {group.title} for {year}
                  </span>

                  <Button type="link" onClick={() => navigate('../info')}>
                    Edit periodicity
                  </Button>
                </Flex>
              ) : group.key === OBLIGATION_GROUPS.annual_report.key ? (
                `${group.title} for ${year - 1}`
              ) : (
                `${group.title} for ${year}`
              )
            }
            key={`${year}-${group.key}`}
          >
            <TaxTable
              taxesInGroup={taxesInGroup}
              isLoading={isLoading}
              groupTitle={group.title}
              year={year}
              onOpenEditModal={handleOpenEditModal}
              onRefetch={refetchObligations}
            />
          </Card>
        );
      })}
    </Flex>
  );

  return (
    <Flex gap={30} vertical>
      <Flex justify="space-between" align="center">
        <S.Title>VAT & Taxes</S.Title>

        <Button
          icon={<PlusOutlined />}
          type="primary"
          size="large"
          onClick={() => handleOpenEditModal()}
        >
          Add new
        </Button>
      </Flex>

      <Tabs
        activeKey={activeKey}
        size="large"
        items={years.map((year) => ({
          key: year.toString(),
          label: year.toString(),
          children: renderObligationGroupTables(year),
        }))}
        onChange={setActiveKey}
      />

      <TaxEditModal
        tax={selectedObligation}
        isOpen={isModalVisible}
        onClose={handleCloseModal}
        onCreate={handleCreateObligation}
        onUpdate={handleUpdateObligation}
        onDelete={handleDeleteObligation}
        isLoading={isFormLoading}
      />
    </Flex>
  );
};
